/* eslint-disable react/display-name */
import React, { useState, forwardRef } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { ListItem, Button, Collapse, Tooltip, colors } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useTranslation } from "react-i18next";

const CustomRouterLink = forwardRef((props, ref) => (
  <div
    ref={ref}
    style={{ flexGrow: 1 }}
  >
    <RouterLink {...props} />
  </div>
));

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%'
  },
  buttonLeaf: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  icon: {
    color: theme.palette.icon,
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  buttonCollapsed: {
    justifyContent: 'center',
    minWidth: 0
  },
  iconCollapsed: {
    marginRight: 0
  },
  expandIcon: {
    marginLeft: 'auto',
    height: 16,
    width: 16
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  active: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    '& $icon': {
      color: theme.palette.primary.main
    }
  }
}));

const NavigationListItem = props => {
  const {
    title,
    href,
    depth,
    children,
    icon: Icon,
    className,
    collapsed,
    onExpand,
    open: openProp,
    label: Label,
    ...rest
  } = props;

  const classes = useStyles();
  const [open, setOpen] = useState(openProp);
  const [t] = useTranslation();

  const handleToggle = () => {
    setOpen(open => !open);
  };

  let paddingLeft = 8;

  // A collapsed rail is 72px wide; indenting inside it would push the icon
  // off-centre for no gain, and its children are not rendered anyway.
  if (depth > 0 && !collapsed) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = {
    paddingLeft: collapsed ? 0 : paddingLeft
  };

  /**
   * Collapsed, the label is gone and the icon has to carry the whole meaning.
   * The tooltip is what makes that legible — without it the rail is a column
   * of guesses.
   */
  const withTooltip = button => collapsed ? (
    <Tooltip
      placement="right"
      title={t(title)}
    >
      {button}
    </Tooltip>
  ) : button;

  if (children) {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.item, className)}
        disableGutters
      >
        {withTooltip(
          <Button
            className={clsx(classes.button, collapsed && classes.buttonCollapsed)}
            onClick={collapsed ? onExpand : handleToggle}
            style={style}
          >
            {Icon && <Icon className={clsx(classes.icon, collapsed && classes.iconCollapsed)} />}
            {!collapsed && t(title)}
            {/* No submenu is rendered while collapsed, so nothing claims to open. */}
            {!collapsed && (open ? (
              <ExpandLessIcon
                className={classes.expandIcon}
                color="inherit"
              />
            ) : (
              <ExpandMoreIcon
                className={classes.expandIcon}
                color="inherit"
              />
            ))}
          </Button>
        )}
        <Collapse in={open && !collapsed}>{children}</Collapse>
      </ListItem>
    );
  } else {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.itemLeaf, className)}
        disableGutters
      >
        {withTooltip(
          <Button
            activeClassName={classes.active}
            className={clsx(classes.buttonLeaf, `depth-${depth}`, collapsed && classes.buttonCollapsed)}
            component={CustomRouterLink}
            exact
            style={style}
            to={href}
          >
            {Icon && <Icon className={clsx(classes.icon, collapsed && classes.iconCollapsed)} />}
            {!collapsed && t(title)}
            {!collapsed && Label && (
              <span className={classes.label}>
                <Label />
              </span>
            )}
          </Button>
        )}
      </ListItem>
    );
  }
};

NavigationListItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any,
  label: PropTypes.any,
  onExpand: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired
};

NavigationListItem.defaultProps = {
  collapsed: false,
  depth: 0,
  open: false
};

export default NavigationListItem;
